import { Dropdown, Input, InputRef, MenuProps } from 'antd';
import { MenuItemType } from 'antd/es/menu/interface';
import { ReactNode } from 'react';
import { Log } from 'src/service/log.service';
import { GLOB } from 'src/util/Glob';
import { createNode, deleteNode, renameNode } from './hierarchyMutations';
import { CreatableType, HierarchyAccessors, HierarchyAction, HierarchyDomain, TreeCtx } from './hierarchyTypes';

export interface TreeMenuConfig<T> {
  acc: HierarchyAccessors<T>;
  domain: HierarchyDomain;
  ctx: TreeCtx<T>;
  relabel: (live: T, label: string) => void;
  onModalOpen?: () => void;
  onModalClose?: () => void;
  deleteContent?: (label: string) => ReactNode;
  decorateChild?: (child: T) => void;
  onCreated?: (child: T | undefined, type: CreatableType) => void;
  onDeleted?: (node: T) => void;
}

const CREATABLE: CreatableType[] = [HierarchyAction.FOLDER, HierarchyAction.DASHBOARD, HierarchyAction.GROUP];

function nameInput(initial: string, onChange: (v: string) => void): ReactNode {
  return (
    <Input
      defaultValue={initial}
      placeholder="Enter new name"
      ref={(r: InputRef | null) => { if (r) setTimeout(() => r.input?.focus()); }}
      onChange={e => onChange(e.target.value)}
      onPressEnter={e => e.currentTarget.closest('.ant-modal-body')?.querySelector<HTMLButtonElement>('.ant-btn-primary')?.click()}
    />
  );
}

function createDialog<T extends object>(parent: T, type: CreatableType, cfg: TreeMenuConfig<T>) {
  let name = '';
  cfg.onModalOpen?.();
  GLOB.app.modal.confirm({
    title: <span>Create new {type} inside <strong>{cfg.acc.getLabel(parent)}</strong></span>,
    icon: null,
    autoFocusButton: null,
    zIndex: 2000,
    content: nameInput('', v => { name = v; }),
    okText: 'Create',
    onOk: () => createNode(parent, type, name, cfg),
    afterClose: () => cfg.onModalClose?.(),
  });
}

function renameDialog<T>(node: T, type: string, cfg: TreeMenuConfig<T>) {
  let name = cfg.acc.getLabel(node);
  cfg.onModalOpen?.();
  GLOB.app.modal.confirm({
    title: `Rename ${type}`,
    icon: null,
    autoFocusButton: null,
    zIndex: 2000,
    content: nameInput(name, v => { name = v; }),
    okText: 'Rename',
    onOk: () => renameNode(node, type, name, cfg),
    afterClose: () => cfg.onModalClose?.(),
  });
}

function deleteDialog<T>(node: T, type: string, cfg: TreeMenuConfig<T>) {
  const label = cfg.acc.getLabel(node);
  cfg.onModalOpen?.();
  GLOB.app.modal.confirm({
    title: 'Confirm delete',
    autoFocusButton: null,
    zIndex: 2000,
    content: cfg.deleteContent?.(label) ?? <span>Are you sure to delete {type} <strong>{label}</strong>?</span>,
    okText: 'Delete',
    onOk: () => deleteNode(node, type, cfg),
    afterClose: () => cfg.onModalClose?.(),
  });
}

// Resolve the node's hierarchy type for rename/delete URLs.
function nodeType<T>(node: T, acc: HierarchyAccessors<T>): string {
  if (acc.isDashboard(node)) return HierarchyAction.DASHBOARD;
  if (acc.isGroup(node)) return HierarchyAction.GROUP;
  return HierarchyAction.FOLDER;
}

export function buildCreateItems<T>(node: T, cfg: TreeMenuConfig<T>): MenuItemType[] {
  const { acc, domain } = cfg;
  const { readonly } = GLOB.userInfo;
  const isDash = acc.isDashboard(node);
  const isGroup = acc.isGroup(node);
  const lock = readonly && acc.getGroupId(node) != null;
  const has = (a: HierarchyAction) => domain.actions.includes(a);
  const items: MenuItemType[] = [];
  if (has(HierarchyAction.FOLDER)) items.push({ key: HierarchyAction.FOLDER, disabled: lock || isDash || isGroup, label: 'Create folder' });
  if (has(HierarchyAction.DASHBOARD)) items.push({ key: HierarchyAction.DASHBOARD, disabled: lock || isDash || isGroup, label: 'Create dashboard' });
  if (has(HierarchyAction.GROUP)) items.push({ key: HierarchyAction.GROUP, disabled: lock || !isDash, label: 'Create group' });
  return items;
}

function menuFor<T extends object>(node: T, cfg: TreeMenuConfig<T>): MenuProps {
  const { acc, domain } = cfg;
  const { readonly } = GLOB.userInfo;
  // Shared-branch roots are managed via group administration — their id is a group id,
  // not a folder/dashboard id, so rename/delete URLs would be bogus.
  const isRoot = acc.isRoot(node) || acc.isGroupRoot(node);
  const lock = readonly && acc.getGroupId(node) != null;
  const has = (a: HierarchyAction) => domain.actions.includes(a);
  const items = buildCreateItems(node, cfg);
  if (has(HierarchyAction.RENAME)) items.push({ key: HierarchyAction.RENAME, disabled: lock || isRoot, label: 'Rename' });
  if (has(HierarchyAction.DELETE)) items.push({ key: HierarchyAction.DELETE, disabled: lock || isRoot, label: 'Delete' });

  return {
    items,
    onClick: info => {
      info.domEvent.preventDefault();
      info.domEvent.stopPropagation();
      const key = info.key as HierarchyAction;
      if (CREATABLE.includes(key as CreatableType)) createDialog(node, key as CreatableType, cfg);
      else if (key === HierarchyAction.RENAME) renameDialog(node, nodeType(node, acc), cfg);
      else if (key === HierarchyAction.DELETE) deleteDialog(node, nodeType(node, acc), cfg);
      else Log.warn(`Unknown context action ${info.key}`);
    },
  };
}

export function withContextMenu<T extends object>(node: T, title: ReactNode, cfg: TreeMenuConfig<T>): ReactNode {
  return (
    <Dropdown
      menu={menuFor(node, cfg)}
      trigger={['contextMenu']}
      popupRender={menu => <div onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}>{menu}</div>}
    >
      <span>{title}</span>
    </Dropdown>
  );
}

export function createDialogFor<T extends object>(parent: T, type: CreatableType, cfg: TreeMenuConfig<T>) {
  createDialog(parent, type, cfg);
}

export function renameDialogFor<T>(node: T, type: string, cfg: TreeMenuConfig<T>) {
  renameDialog(node, type, cfg);
}

export function deleteDialogFor<T>(node: T, type: string, cfg: TreeMenuConfig<T>) {
  deleteDialog(node, type, cfg);
}
